import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  SignInIcon as SignIn,
  CircleNotchIcon as Spinner,
  WarningCircleIcon as WarningCircle,
} from "@phosphor-icons/react";
import { formatApiErrorDetail, useAuth } from "@/lib/auth";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const { state } = useLocation();
  const dest = state?.from || "/workspaces";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // If already authenticated when this page mounts, bounce to destination.
  useEffect(() => {
    if (user) navigate(dest, { replace: true });
  }, [user, dest, navigate]);

  if (user === undefined) {
    return <CheckingFallback />;
  }
  if (user) {
    return <Navigate to={dest} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 bg-[var(--ml-bg)]"
      data-testid="login-page"
    >
      <form
        onSubmit={onSubmit}
        className="ml-card w-full max-w-md p-8 space-y-5"
        data-testid="login-form"
      >
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5 mb-1">
            <SignIn size={11} weight="bold" />
            Sign in
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Welcome back to Modernization Lab
          </h1>
          <p className="text-sm text-[var(--ml-text-secondary)] mt-1">
            Sign in to access your engagements and findings.
          </p>
        </div>

        <Field
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={setEmail}
          testid="login-email"
          required
        />
        <Field
          label="Password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={setPassword}
          testid="login-password"
          required
        />

        {error && (
          <div
            className="flex items-start gap-2 text-sm text-[#a2191f] bg-[#fff1f1] border border-[#ffd7d9] px-3 py-2"
            data-testid="login-error"
            role="alert"
          >
            <WarningCircle size={14} weight="bold" className="mt-0.5 shrink-0" />
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !email || !password}
          data-testid="login-submit"
          className="w-full px-4 py-2 bg-[var(--ml-primary)] text-white font-semibold hover:bg-[var(--ml-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {submitting ? <Spinner size={16} className="animate-spin" /> : null}
          {submitting ? "Signing in…" : "Sign in"}
        </button>

        <div className="text-sm text-[var(--ml-text-secondary)] text-center">
          <Link
            to="/forgot-password"
            className="text-[var(--ml-primary)] hover:underline"
            data-testid="login-to-forgot-password"
          >
            Forgot your password?
          </Link>
        </div>

        <div className="text-sm text-[var(--ml-text-secondary)] text-center">
          Need an account?{" "}
          <Link
            to="/register"
            className="text-[var(--ml-primary)] hover:underline font-semibold"
            data-testid="login-to-register"
          >
            Create one
          </Link>
        </div>
      </form>
    </div>
  );
}

// ---- helpers ------------------------------------------------------------
function Field({ label, type, autoComplete, value, onChange, testid, required }) {
  return (
    <label className="block">
      <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1">
        {label}
      </div>
      <input
        type={type}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="w-full px-3 py-2 border border-[var(--ml-border)] focus:outline-none focus:border-[var(--ml-primary)] text-sm"
      />
    </label>
  );
}

export function CheckingFallback() {
  return (
    <div
      className="min-h-screen flex items-center justify-center"
      data-testid="auth-checking"
    >
      <div className="flex items-center gap-2 text-sm text-[var(--ml-text-secondary)]">
        <Spinner size={14} className="animate-spin" /> Checking session…
      </div>
    </div>
  );
}
